import React, { useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { ComunicacaoUsuarioViewModel } from '../../viewmodel/banca/ComunicacaoUsuario/ComunicacaoUsuarioViewModel';
import Logo from '../../assets/img/logo-color.png';
import BackendErrorMessage from '../../components/BackendErrorMessage';

interface EmailConfirmationResponse {
  mensagemPrincipal: string;
  mensagemAuxiliar?: string;
  tipoMensagemAuxiliar?: string;
}

const ConfirmaEmail: React.FC = () => {
  const location = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const token = useMemo(() => queryParams.get('token'), [queryParams]);

  const [confirmado, setConfirmado] = useState<boolean | null>(null);
  const [erroConfirmacao, setErroConfirmacao] = useState<any | null>(null);
  const [responseData, setResponseData] = useState<EmailConfirmationResponse | null>(null);
  const [fadeIn, setFadeIn] = useState(false);

  useEffect(() => {
    const fetchConfirmaToken = async () => {
      try {
        const response = await ComunicacaoUsuarioViewModel.confirmaToken(token!);
        setConfirmado(true);
        setResponseData(response);
      } catch (error) {
        setConfirmado(false);
        setErroConfirmacao(BackendErrorMessage(error));
      }
      setTimeout(() => setFadeIn(true), 100);
    };

    if (token) {
      fetchConfirmaToken();
    }
  }, [token]);

  const getStatusColor = () => {
    if (confirmado === true) return '#22c55e';
    if (erroConfirmacao?.includes('anteriormente')) return '#f59e0b';
    return '#ef4444';
  };

  const getStatusIcon = () => {
    if (confirmado === true) return '✓';
    if (erroConfirmacao?.includes('anteriormente')) return '⚠';
    return '✕';
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <div style={{
        maxWidth: '480px',
        width: '100%',
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        padding: '3rem',
        textAlign: 'center',
        transform: fadeIn ? 'translateY(0)' : 'translateY(20px)',
        opacity: fadeIn ? 1 : 0,
        transition: 'all 0.6s ease-out'
      }}>
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{
            fontSize: '2rem',
            fontWeight: '700',
            color: '#1e293b',
            margin: '0 0 0.5rem 0'
          }}>Identifica</h1>
          <p style={{
            color: '#64748b',
            fontSize: '0.875rem',
            margin: 0,
            lineHeight: '1.4'
          }}>Sistema de Heteroidentificação de Cotas Étnico-Raciais</p>
        </div>

        {confirmado === null ? (
          <div style={{ padding: '3rem 0' }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '3px solid #e2e8f0',
              borderTop: '3px solid #3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 1rem'
            }} />
            <p style={{ color: '#64748b', margin: 0 }}>Processando confirmação...</p>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        ) : (
          <div style={{
            opacity: fadeIn ? 1 : 0,
            transform: fadeIn ? 'scale(1)' : 'scale(0.95)',
            transition: 'all 0.4s ease-out 0.2s'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: getStatusColor(),
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 auto 1.5rem',
              animation: 'bounce 0.6s ease-out'
            }}>
              {getStatusIcon()}
            </div>
            
            <div style={{
              padding: '1.5rem',
              backgroundColor: '#f8fafc',
              borderRadius: '12px',
              marginBottom: '1.5rem',
              border: `2px solid ${getStatusColor()}20`
            }}>
              <p style={{
                color: '#1e293b',
                fontSize: '1.125rem',
                fontWeight: '600',
                margin: 0,
                whiteSpace: 'pre-line'
              }}>
                {confirmado ? responseData?.mensagemPrincipal : erroConfirmacao}
              </p>
            </div>

            {confirmado && responseData?.mensagemAuxiliar && (
              <div style={{
                padding: '1.25rem',
                backgroundColor: '#f8fafc',
                borderRadius: '12px',
                border: '2px solid #64748b20',
                marginBottom: '1.5rem'
              }}>
                <p style={{
                  color: '#475569',
                  fontSize: '0.9rem',
                  margin: 0,
                  whiteSpace: 'pre-line',
                  lineHeight: '1.6',
                  fontWeight: '500'
                }}>
                  {responseData.mensagemAuxiliar}
                </p>
              </div>
            )}

            <img 
              src={Logo} 
              alt="Logo Identifica" 
              style={{
                maxWidth: '120px',
                height: 'auto',
                opacity: 0.8
              }}
            />
            
            <style>{`
              @keyframes bounce {
                0%, 20%, 53%, 80%, 100% {
                  transform: translate3d(0,0,0);
                }
                40%, 43% {
                  transform: translate3d(0, -8px, 0);
                }
                70% {
                  transform: translate3d(0, -4px, 0);
                }
                90% {
                  transform: translate3d(0, -2px, 0);
                }
              }
            `}</style>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConfirmaEmail;